import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, of, switchMap } from 'rxjs';

import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import { markCached } from '@ps/utils';
import { MentionableUsersService } from '@ps/survey-data-access';

import { MENTIONABLE_USERS_CACHE_TTL_MS } from './mentionable-users.constants';
import {
  MentionableUsersActions,
  MentionableUsersApiActions,
} from './mentionable-users.actions';
import { selectMentionableUsersCachedAt } from './mentionable-users.selectors';

@Injectable()
export class MentionableUsersEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _mentionableUsersService = inject(MentionableUsersService);

  /**
   * Any failure (most realistically the buyer's company lacking the
   * enableNotificationCenter flag, a 403) degrades silently to an empty
   * candidate list rather than an error state — per spec, the @mention
   * feature is simply unavailable, with no toast or banner. No failure
   * action exists for this slice for that reason.
   */
  public readonly loadMentionableUsers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(MentionableUsersActions.loadMentionableUsers),
      withCacheGate(
        this._store.select(selectMentionableUsersCachedAt),
        MENTIONABLE_USERS_CACHE_TTL_MS,
      ),
      switchMap(() =>
        this._mentionableUsersService.mentionableUsers().pipe(
          map((users) =>
            MentionableUsersApiActions.loadMentionableUsersSuccess({
              users,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError(
            () =>
              of(
                MentionableUsersApiActions.loadMentionableUsersSuccess({
                  users: [],
                  cachedAt: markCached(),
                }),
              ),
            { notify: false },
          ),
        ),
      ),
    ),
  );
}
